import React from "react";
import tpIcon from "../../assets/images/tp-icon.png";
import Avatar from "../common/Avatar";
import ClubCrest from "../common/ClubCrest";
import NationalityFlags from "../common/NationalityFlags";
import {
  formatRating,
  ratingTier,
  placePlayers,
  playerInitials,
  positionDetailParts,
  standoutOf,
  SKELETON_SLOTS,
} from "./totwUtils";

// =====================================================================
// TOTW — the hero pitch (frames 1a/1b/1d desktop · 1f/1h/1i mobile).
// Vertical pitch, GK at the bottom; 1–11 cards placed by location_x/y
// (0-100 -> % positioning) with the design's collision spreader.
// Desktop cards: 96px, avatar 44, rating chip; mobile: 48px, avatar 22.
// Represented players carry a blue border + docked TP icon; the round's
// standout carries the "Standout" pill. Popover on hover (desktop) / tap
// (mobile) with View profile + (represented only) Contact.
// =====================================================================

const PITCH_LINE = "rgba(255,255,255,0.16)";

const PitchMarkings = ({ mobile }) => {
  const inset = mobile ? 9 : 14;
  const line = mobile ? "1.2px" : "1.5px";
  return (
    <>
      <div
        className="absolute inset-0"
        style={{
          background: `repeating-linear-gradient(180deg, rgba(255,255,255,0.022) 0px, rgba(255,255,255,0.022) ${mobile ? 40 : 52}px, rgba(255,255,255,0) ${mobile ? 40 : 52}px, rgba(255,255,255,0) ${mobile ? 80 : 104}px)`,
        }}
      />
      <div className="absolute rounded-[3px]" style={{ inset, border: `${line} solid ${PITCH_LINE}` }} />
      <div className="absolute" style={{ left: inset, right: inset, top: "50%", height: line, background: PITCH_LINE }} />
      <div
        className="absolute rounded-full"
        style={{ left: "50%", top: "50%", width: mobile ? 78 : 120, height: mobile ? 78 : 120, border: `${line} solid ${PITCH_LINE}`, transform: "translate(-50%,-50%)" }}
      />
      <div className="absolute rounded-full" style={{ left: "50%", top: "50%", width: 7, height: 7, background: "rgba(255,255,255,0.28)", transform: "translate(-50%,-50%)" }} />
      {/* bottom penalty box + six-yard box + goal */}
      <div className="absolute" style={{ left: "50%", bottom: inset, width: mobile ? 168 : 270, height: mobile ? 74 : 118, border: `${line} solid ${PITCH_LINE}`, borderBottom: "none", transform: "translateX(-50%)" }} />
      <div className="absolute" style={{ left: "50%", bottom: inset, width: mobile ? 82 : 130, height: mobile ? 30 : 48, border: `${line} solid ${PITCH_LINE}`, borderBottom: "none", transform: "translateX(-50%)" }} />
      <div className="absolute rounded-t-[2px]" style={{ left: "50%", bottom: mobile ? 5 : 8, width: mobile ? 50 : 76, height: mobile ? 5 : 8, background: "rgba(255,255,255,0.22)", transform: "translateX(-50%)" }} />
      {/* top penalty box */}
      <div className="absolute" style={{ left: "50%", top: inset, width: mobile ? 168 : 270, height: mobile ? 74 : 118, border: `${line} solid ${PITCH_LINE}`, borderTop: "none", transform: "translateX(-50%)" }} />
      {!mobile && (
        <div className="absolute" style={{ left: "50%", top: inset, width: 130, height: 48, border: `${line} solid ${PITCH_LINE}`, borderTop: "none", transform: "translateX(-50%)" }} />
      )}
    </>
  );
};

const PitchSkeleton = ({ mobile }) => {
  const slots = placePlayers(SKELETON_SLOTS, mobile ? "mobile" : "desktop");
  return (
    <div>
      {slots.map((slot) => (
        <div
          key={slot.ts_player_id}
          className="absolute flex flex-col items-center animate-pulse"
          style={{ left: `${slot._left}%`, top: `${slot._top}%`, transform: "translate(-50%,-50%)", gap: mobile ? 5 : 6 }}
        >
          <div className="rounded-full" style={{ width: mobile ? 34 : 46, height: mobile ? 34 : 46, background: "rgba(255,255,255,0.16)" }} />
          <div className="rounded" style={{ width: mobile ? 44 : 62, height: mobile ? 8 : 9, background: "rgba(255,255,255,0.13)" }} />
          {!mobile && <div className="rounded" style={{ width: 32, height: 9, background: "rgba(255,255,255,0.1)" }} />}
        </div>
      ))}
    </div>
  );
};

const RatingChip = ({ rating, mobile }) => {
  const tier = ratingTier(rating);
  const label = formatRating(rating);
  if (label == null) return null;
  return (
    <div
      data-no-translate="true"
      className="font-extrabold tabular-nums"
      style={{
        background: tier.bg,
        color: tier.fg,
        fontSize: mobile ? 8 : 11,
        borderRadius: mobile ? 4 : 6,
        padding: mobile ? "0.5px 3.5px" : "1px 7px",
      }}
    >
      {label}
    </div>
  );
};

const PlayerCard = ({ player, mobile, active, isStandout, onSelect, onHover }) => {
  // Blue border echoes the on-platform badge (two-tier decision: the card
  // badge = on_platform; Contact in the popover = represented only).
  const borderColor = active ? "#0F172A" : player.on_platform ? "#2283FF" : "#E7ECF2";
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={player.name || player.short_name || ""}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(player);
      }}
      onMouseEnter={onHover ? () => onHover(player) : undefined}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(player);
        }
      }}
      className="absolute cursor-pointer"
      style={{
        left: `${player._left}%`,
        top: `${player._top}%`,
        transform: "translate(-50%,-50%)",
        width: mobile ? 48 : 96,
        zIndex: active ? 30 : 10,
      }}
    >
      {isStandout && (
        <div
          className="absolute left-1/2 -translate-x-1/2 z-[3] bg-[#2283FF] text-white font-extrabold uppercase whitespace-nowrap rounded-full"
          style={{
            top: mobile ? -9 : -10,
            fontSize: mobile ? 7.5 : 8.5,
            letterSpacing: "0.08em",
            padding: mobile ? "1.5px 5px" : "2px 8px",
            boxShadow: "0 2px 6px rgba(4,18,33,0.3)",
          }}
        >
          Standout
        </div>
      )}
      <div
        className="relative bg-white flex flex-col items-center"
        style={{
          border: `${mobile ? 1.2 : 1.5}px solid ${borderColor}`,
          borderRadius: mobile ? 8 : 12,
          padding: mobile ? "4px 2px" : "9px 6px 8px",
          gap: mobile ? 2 : 5,
          boxShadow: mobile ? "0 3px 7px rgba(4,18,33,0.3)" : "0 5px 14px rgba(4,18,33,0.34)",
        }}
      >
        <div className="relative">
          <Avatar
            src={player.photo}
            alt={player.name || ""}
            size={mobile ? 22 : 44}
            fallbackText={player.name || player.short_name || "?"}
          />
          <div
            className="absolute bg-white flex items-center justify-center"
            style={{
              right: mobile ? -4 : -6,
              bottom: -4,
              width: mobile ? 12 : 21,
              height: mobile ? 12 : 21,
              borderRadius: mobile ? 4 : 7,
              border: "1px solid #E2E8F0",
              boxShadow: "0 1px 3px rgba(4,18,33,0.18)",
            }}
          >
            <ClubCrest
              name={player.team?.name || ""}
              logoUrl={player.team?.logo || null}
              variant="shield"
              width={mobile ? 8 : 15}
              height={mobile ? 9 : 17}
            />
          </div>
        </div>
        <div
          data-no-translate="true"
          className="font-bold text-center whitespace-nowrap overflow-hidden text-ellipsis text-[#0F172A]"
          style={{ maxWidth: mobile ? 44 : 84, fontSize: mobile ? 8 : 11.5 }}
        >
          {player.short_name || player.name || playerInitials(player)}
        </div>
        <RatingChip rating={player.rating} mobile={mobile} />
        {player.on_platform && (
          <img
            src={tpIcon}
            alt="On TransferPitch"
            className="absolute block bg-white"
            style={{
              top: mobile ? -7 : -9,
              right: mobile ? -7 : -9,
              width: mobile ? 17 : 24,
              height: mobile ? 17 : 24,
              borderRadius: mobile ? 6 : 8,
              boxShadow: "0 0 0 1.5px #FFFFFF, 0 2px 5px rgba(4,18,33,0.28)",
            }}
          />
        )}
      </div>
    </div>
  );
};

const Popover = ({ player, mobile, onViewProfile, onContact }) => {
  // Clamp horizontally; flip above/below depending on the rendered position
  // (cards in the lower half open upwards, upper half downwards).
  const clamp = mobile ? [32, 68] : [26, 74];
  const left = Math.min(clamp[1], Math.max(clamp[0], player._left));
  const below = player._top < 50;
  const offset = mobile ? 38 : 46;
  // FLAGS ONLY, never country-code text (Marin finding 1: "wir nutzen
  // AUSSCHLIESSLICH Landesflaggen"), ALL citizenships (finding 3). Preferred
  // source: the ordered `nationalities` list ({id, name, image_url}, national
  // team first — same mechanic as the search table / NationalityFlags).
  // Legacy payloads without it fall back to the old nationality_logo pair;
  // a name without any flag image renders nothing rather than a code.
  const nationalityList =
    Array.isArray(player.nationalities) && player.nationalities.length
      ? player.nationalities
      : player.nationality_logo
        ? [{ name: player.nationality || "", image_url: player.nationality_logo }]
        : [];
  const detail = positionDetailParts(player.position_detail);
  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className="absolute z-[60] bg-white"
      style={{
        left: `${left}%`,
        top: `${player._top}%`,
        transform: below ? `translate(-50%, ${offset}px)` : `translate(-50%, calc(-100% - ${offset}px))`,
        width: mobile ? 224 : 264,
        border: "1px solid #E2E8F0",
        borderRadius: mobile ? 13 : 14,
        boxShadow: "0 22px 46px rgba(4,18,33,0.34)",
        padding: mobile ? 12 : 14,
      }}
    >
      <div className="flex items-center" style={{ gap: mobile ? 10 : 11 }}>
        <Avatar
          src={player.photo}
          alt={player.name || ""}
          size={mobile ? 40 : 46}
          fallbackText={player.name || player.short_name || "?"}
        />
        <div className="flex flex-col min-w-0 flex-1" style={{ gap: 3 }}>
          <div data-no-translate="true" className="font-extrabold text-[#0F172A]" style={{ fontSize: mobile ? 13.5 : 14.5, letterSpacing: "-0.01em" }}>
            {player.name || player.short_name || playerInitials(player)}
          </div>
          <div className="flex items-center text-[#64748B] font-semibold" style={{ gap: mobile ? 5 : 6, fontSize: mobile ? 11.5 : 12 }}>
            <ClubCrest
              name={player.team?.name || ""}
              logoUrl={player.team?.logo || null}
              variant="shield"
              width={mobile ? 13 : 14}
              height={mobile ? 15 : 16}
            />
            <span data-no-translate="true" className="whitespace-nowrap overflow-hidden text-ellipsis">
              {player.team?.name || ""}
            </span>
          </div>
        </div>
        <span
          data-no-translate="true"
          className="flex-none self-start font-extrabold tabular-nums"
          style={{
            background: ratingTier(player.rating).bg,
            color: ratingTier(player.rating).fg,
            fontSize: mobile ? 12 : 13,
            borderRadius: mobile ? 6 : 7,
            padding: mobile ? "2px 8px" : "3px 9px",
          }}
        >
          {formatRating(player.rating)}
        </span>
      </div>
      <div className="flex items-center flex-wrap" style={{ gap: mobile ? 5 : 6, margin: mobile ? "10px 0" : "11px 0" }}>
        {detail ? (
          // Finding 2: detailed position when the data knows it — main
          // position bold, side positions muted. Position codes are data,
          // not copy (data-no-translate).
          <span
            data-no-translate="true"
            className="bg-[#F1F5F9] text-[#334155] rounded-full"
            style={{ fontSize: mobile ? 10.5 : 11, padding: mobile ? "3px 8px" : "4px 9px" }}
          >
            <span className="font-extrabold">{detail.primary}</span>
            {detail.others.length > 0 && (
              <span className="font-semibold text-[#94A3B8]"> · {detail.others.join(" · ")}</span>
            )}
          </span>
        ) : (
          // Fallback: the coarse lineup line, exactly as before.
          <span className="bg-[#F1F5F9] text-[#334155] font-bold rounded-full" style={{ fontSize: mobile ? 10.5 : 11, padding: mobile ? "3px 8px" : "4px 9px" }}>
            {positionLabel(player.position)}
          </span>
        )}
        {nationalityList.length > 0 && (
          <span className="flex items-center bg-[#F1F5F9] rounded-full" style={{ padding: mobile ? "4px 8px" : "5px 9px" }}>
            <NationalityFlags
              nationalities={nationalityList}
              className={
                mobile
                  ? "w-4 h-3 object-cover border border-gray-200 rounded-sm"
                  : "w-5 h-4 object-cover border border-gray-200 rounded-sm"
              }
              wrapperClassName="flex flex-wrap items-center gap-1.5"
            />
          </span>
        )}
      </div>
      <div className="flex flex-col" style={{ gap: mobile ? 7 : 8 }}>
        {player.represented && (
          <div
            className="flex items-center bg-[#F4F9FF] text-[#1257B0] font-bold"
            style={{ gap: mobile ? 6 : 7, border: "1px solid #D6E9FF", borderRadius: 9, padding: mobile ? "7px 9px" : "8px 10px", fontSize: mobile ? 11 : 11.5 }}
          >
            <img src={tpIcon} alt="TransferPitch" className="block" style={{ width: mobile ? 18 : 19, height: mobile ? 18 : 19 }} />
            <span>Represented on TransferPitch</span>
          </div>
        )}
        {player.wyscout_id != null && (
          <button
            type="button"
            onClick={() => onViewProfile(player)}
            className="flex items-center justify-center bg-[#2283FF] hover:bg-[#0B6BE0] text-white font-extrabold cursor-pointer"
            style={{ gap: mobile ? 5 : 6, height: mobile ? 46 : 42, borderRadius: 10, fontSize: mobile ? 13.5 : 13 }}
          >
            View profile <span className="font-extrabold">›</span>
          </button>
        )}
        {player.represented && player.agent_account_id != null && (
          <button
            type="button"
            onClick={() => onContact(player)}
            className="flex items-center justify-center bg-white hover:border-[#2283FF] hover:text-[#2283FF] text-[#0F172A] font-extrabold cursor-pointer"
            style={{ height: mobile ? 44 : 40, borderRadius: 10, fontSize: mobile ? 13 : 12.5, border: "1px solid #E2E8F0" }}
          >
            Contact
          </button>
        )}
      </div>
    </div>
  );
};

const positionLabel = (position) => {
  switch (position) {
    case "G":
      return "Goalkeeper";
    case "D":
      return "Defence";
    case "F":
      return "Attack";
    default:
      return "Midfield";
  }
};

const TOTWPitch = ({
  players = [],
  loading = false,
  mobile = false,
  activeId = null,
  onSelect,
  onHover, // desktop: popover opens on hover (brief: hover desktop / tap mobile)
  onLeave,
  onViewProfile,
  onContact,
}) => {
  const placed = placePlayers(players, mobile ? "mobile" : "desktop");
  const standout = standoutOf(placed);
  const active = placed.find((p) => p.ts_player_id === activeId) || null;
  return (
    <div
      className="relative w-full overflow-hidden bg-[#0B2338]"
      style={{ aspectRatio: mobile ? "68/100" : "68/105", borderRadius: mobile ? 10 : 12 }}
      onMouseLeave={onLeave}
    >
      <PitchMarkings mobile={mobile} />
      {loading ? (
        <PitchSkeleton mobile={mobile} />
      ) : (
        <div>
          {placed.map((player) => (
            <PlayerCard
              key={player.ts_player_id}
              player={player}
              mobile={mobile}
              active={player.ts_player_id === activeId}
              isStandout={standout && player.ts_player_id === standout.ts_player_id}
              onSelect={onSelect}
              onHover={onHover}
            />
          ))}
        </div>
      )}
      {!loading && active && (
        <Popover player={active} mobile={mobile} onViewProfile={onViewProfile} onContact={onContact} />
      )}
    </div>
  );
};

export default TOTWPitch;
